import type { Page, Paged } from "../../api/types";

/**
 * Offset-based pagination for catalog grids / tables (page, pageSize, total).
 * `page` is 1-based; out-of-range pages return an empty `items` array rather
 * than throwing.
 */
export function paginateOffset<T>(items: T[], page: number, pageSize: number): Paged<T> {
  const safePage = Math.max(1, Math.floor(page) || 1);
  const safeSize = Math.max(1, Math.floor(pageSize) || 1);
  const start = (safePage - 1) * safeSize;
  return {
    items: items.slice(start, start + safeSize),
    total: items.length,
    page: safePage,
    pageSize: safeSize,
  };
}

/**
 * Cursor-based pagination for feeds / infinite lists. The cursor is simply
 * the string form of the next offset — opaque to callers, stable across
 * calls as long as the underlying list doesn't shrink.
 */
export function paginateCursor<T>(items: T[], cursor: string | null | undefined, limit: number): Page<T> {
  const offset = cursor ? Math.max(0, Number.parseInt(cursor, 10) || 0) : 0;
  const safeLimit = Math.max(1, Math.floor(limit) || 1);
  const slice = items.slice(offset, offset + safeLimit);
  const nextOffset = offset + slice.length;
  return {
    items: slice,
    nextCursor: nextOffset < items.length ? String(nextOffset) : null,
  };
}
